import { type MouseEvent } from "react";
import { Link } from "react-router";
import { useReducedMotion } from "framer-motion";
import { Button } from "@/shared/components/ui/Button";
import { useMe } from "@/shared/lib/auth";
import { cn } from "@/shared/lib/utils";

interface MarketingCtasProps {
  /** Label for the primary sign-up button (signed-out visitors). */
  primaryLabel: string;
  /** Label for the optional secondary outline button. Omit for a lone CTA. */
  secondaryLabel?: string;
  /** Router destination for the secondary button. */
  secondaryTo?: string;
  /**
   * Element id for the secondary button to smooth-scroll to (in-page). Takes
   * precedence over `secondaryTo`.
   */
  secondaryScrollTo?: string;
  /**
   * When set, the buttons span the full container width and stack vertically on
   * mobile (`max-sm`), reverting to inline/auto width at `sm`+. Opt-in so the
   * shared Final CTA keeps its content-width buttons.
   */
  fullWidthMobile?: boolean;
  className?: string;
}

/**
 * Auth-aware CTA group for marketing sections. A signed-in visitor sees a single
 * "Go to app" button; everyone else sees the primary sign-up CTA plus an optional
 * secondary action — either a router link or an in-page smooth scroll. Shared by
 * the hero and the closing CTA so the auth branching lives in one place.
 */
export function MarketingCtas({
  primaryLabel,
  secondaryLabel,
  secondaryTo,
  secondaryScrollTo,
  fullWidthMobile = false,
  className,
}: MarketingCtasProps) {
  const me = useMe();
  const reduced = useReducedMotion();
  const buttonWidth = fullWidthMobile ? "max-sm:w-full" : undefined;

  function smoothScroll(event: MouseEvent<HTMLAnchorElement>) {
    if (!secondaryScrollTo) return;
    const target = document.getElementById(secondaryScrollTo);
    if (!target) return; // fall back to the default anchor jump
    event.preventDefault();
    target.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "start",
    });
  }

  function renderSecondary() {
    if (!secondaryLabel) return null;
    if (secondaryScrollTo) {
      return (
        <Button asChild variant="outline" size="lg" className={buttonWidth}>
          <a href={`#${secondaryScrollTo}`} onClick={smoothScroll}>
            {secondaryLabel}
          </a>
        </Button>
      );
    }
    if (secondaryTo) {
      return (
        <Button asChild variant="outline" size="lg" className={buttonWidth}>
          <Link to={secondaryTo}>{secondaryLabel}</Link>
        </Button>
      );
    }
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-4",
        fullWidthMobile && "max-sm:w-full max-sm:flex-col",
        className,
      )}
    >
      {me.data ? (
        <Button asChild size="lg" className={buttonWidth}>
          <Link to="/app/contacts">Go to app</Link>
        </Button>
      ) : (
        <>
          <Button asChild size="lg" className={buttonWidth}>
            <Link to="/auth/signup">{primaryLabel}</Link>
          </Button>
          {renderSecondary()}
        </>
      )}
    </div>
  );
}
